/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {Direction, Directionality} from '@angular/cdk/bidi';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {ESCAPE} from '@angular/cdk/keycodes';
import {TemplatePortal} from '@angular/cdk/portal';
import {Overlay} from './overlay';
import {OverlayRef} from './overlay-ref';
import {OverlayState} from './overlay-state';
import {
  // This import is only used to define a generic type. The current TypeScript version incorrectly
  // considers such imports as unused (https://github.com/Microsoft/TypeScript/issues/14953)
  // tslint:disable-next-line:no-unused-variable
  ConnectedOverlayPositionChange,
  ConnectionPositionPair,
} from './position/connected-position';
import {ConnectedPositionStrategy} from './position/connected-position-strategy';
import {RepositionScrollStrategy, ScrollStrategy} from './scroll/index';
import {Subscription} from 'rxjs/Subscription';
/**
 * Default set of positions for the overlay. Follows the behavior of a dropdown.
 */
const defaultPositionList = [
  new ConnectionPositionPair(
      {originX: 'start', originY: 'bottom'},
      {overlayX: 'start', overlayY: 'top'}),
  new ConnectionPositionPair(
      {originX: 'start', originY: 'top'},
      {overlayX: 'start', overlayY: 'bottom'}),
];
/**
 * Injection token that determines the scroll handling while the connected overlay is open.
 */
export const MD_CONNECTED_OVERLAY_SCROLL_STRATEGY =
    new InjectionToken<() => ScrollStrategy>('md-connected-overlay-scroll-strategy');
/**
 * \@docs-private
 * @param {?} overlay
 * @return {?}
 */
export function MD_CONNECTED_OVERLAY_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay):
    () => RepositionScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}
/**
 * \@docs-private
 */
export const MD_CONNECTED_OVERLAY_SCROLL_STRATEGY_PROVIDER = {
  provide: MD_CONNECTED_OVERLAY_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: MD_CONNECTED_OVERLAY_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
/**
 * Directive applied to an element to make it usable as an origin for an Overlay using a
 * ConnectedPositionStrategy.
 */
export class OverlayOrigin {
/**
 * @param {?} elementRef
 */
constructor(public elementRef: ElementRef) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdk-overlay-origin], [overlay-origin], [cdkOverlayOrigin]',
  exportAs: 'cdkOverlayOrigin',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
}

function OverlayOrigin_tsickle_Closure_declarations() {
/** @type {?} */
OverlayOrigin.decorators;
/**
 * @nocollapse
 * @type {?}
 */
OverlayOrigin.ctorParameters;
/** @type {?} */
OverlayOrigin.prototype.elementRef;
}

/**
 * Directive to facilitate declarative creation of an Overlay using a ConnectedPositionStrategy.
 */
export class ConnectedOverlayDirective implements OnDestroy, OnChanges {
private _overlayRef: OverlayRef;
private _templatePortal: TemplatePortal<any>;
private _hasBackdrop = false;
private _backdropSubscription: Subscription | null;
private _positionSubscription: Subscription;
private _offsetX: number = 0;
private _offsetY: number = 0;
private _position: ConnectedPositionStrategy;
private _escapeListener: Function;
/**
 * Origin for the connected overlay.
 */
origin: OverlayOrigin;
/**
 * Registered connected position pairs.
 */
positions: ConnectionPositionPair[];
/**
 * The offset in pixels for the overlay connection point on the x-axis
 * @return {?}
 */
get offsetX(): number { return this._offsetX; }
/**
 * @param {?} offsetX
 * @return {?}
 */
set offsetX(offsetX: number) {
    this._offsetX = offsetX;
    if (this._position) {
      this._position.withOffsetX(offsetX);
    }
  }
/**
 * The offset in pixels for the overlay connection point on the y-axis
 * @return {?}
 */
get offsetY() { return this._offsetY; }
/**
 * @param {?} offsetY
 * @return {?}
 */
set offsetY(offsetY: number) {
    this._offsetY = offsetY;
    if (this._position) {
      this._position.withOffsetY(offsetY);
    }
  }
/**
 * The width of the overlay panel.
 */
width: number | string;
/**
 * The height of the overlay panel.
 */
height: number | string;
/**
 * The min width of the overlay panel.
 */
minWidth: number | string;
/**
 * The min height of the overlay panel.
 */
minHeight: number | string;
/**
 * The custom class to be set on the backdrop element.
 */
backdropClass: string;
/**
 * Strategy to be used when handling scroll events while the overlay is open.
 */
scrollStrategy: ScrollStrategy =
      this._scrollStrategy();
/**
 * Whether the overlay is open.
 */
open: boolean = false;
/**
 * Whether or not the overlay should attach a backdrop.
 * @return {?}
 */
get hasBackdrop() { return this._hasBackdrop; }
/**
 * @param {?} value
 * @return {?}
 */
set hasBackdrop(value: any) { this._hasBackdrop = coerceBooleanProperty(value); }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedOrigin(): OverlayOrigin { return this.origin; }
/**
 * @param {?} _origin
 * @return {?}
 */
set _deprecatedOrigin(_origin: OverlayOrigin) { this.origin = _origin; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedPositions(): ConnectionPositionPair[] { return this.positions; }
/**
 * @param {?} _positions
 * @return {?}
 */
set _deprecatedPositions(_positions: ConnectionPositionPair[]) { this.positions = _positions; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedOffsetX(): number { return this.offsetX; }
/**
 * @param {?} _offsetX
 * @return {?}
 */
set _deprecatedOffsetX(_offsetX: number) { this.offsetX = _offsetX; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedOffsetY(): number { return this.offsetY; }
/**
 * @param {?} _offsetY
 * @return {?}
 */
set _deprecatedOffsetY(_offsetY: number) { this.offsetY = _offsetY; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedWidth(): number | string { return this.width; }
/**
 * @param {?} _width
 * @return {?}
 */
set _deprecatedWidth(_width: number | string) { this.width = _width; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedHeight(): number | string { return this.height; }
/**
 * @param {?} _height
 * @return {?}
 */
set _deprecatedHeight(_height: number | string) { this.height = _height; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedMinWidth(): number | string { return this.minWidth; }
/**
 * @param {?} _minWidth
 * @return {?}
 */
set _deprecatedMinWidth(_minWidth: number | string) { this.minWidth = _minWidth; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedMinHeight(): number | string { return this.minHeight; }
/**
 * @param {?} _minHeight
 * @return {?}
 */
set _deprecatedMinHeight(_minHeight: number | string) { this.minHeight = _minHeight; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedBackdropClass(): string { return this.backdropClass; }
/**
 * @param {?} _backdropClass
 * @return {?}
 */
set _deprecatedBackdropClass(_backdropClass: string) { this.backdropClass = _backdropClass; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedScrollStrategy(): ScrollStrategy { return this.scrollStrategy; }
/**
 * @param {?} _scrollStrategy
 * @return {?}
 */
set _deprecatedScrollStrategy(_scrollStrategy: ScrollStrategy) {
    this.scrollStrategy = _scrollStrategy;
  }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedOpen(): boolean { return this.open; }
/**
 * @param {?} _open
 * @return {?}
 */
set _deprecatedOpen(_open: boolean) { this.open = _open; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedHasBackdrop() { return this.hasBackdrop; }
/**
 * @param {?} _hasBackdrop
 * @return {?}
 */
set _deprecatedHasBackdrop(_hasBackdrop: any) { this.hasBackdrop = _hasBackdrop; }
/**
 * Event emitted when the backdrop is clicked.
 */
backdropClick = new EventEmitter<void>();
/**
 * Event emitted when the position has changed.
 */
positionChange = new EventEmitter<ConnectedOverlayPositionChange>();
/**
 * Event emitted when the overlay has been attached.
 */
attach = new EventEmitter<void>();
/**
 * Event emitted when the overlay has been detached.
 */
detach = new EventEmitter<void>();
/**
 * @param {?} _overlay
 * @param {?} _renderer
 * @param {?} templateRef
 * @param {?} viewContainerRef
 * @param {?} _scrollStrategy
 * @param {?} _dir
 */
constructor(
private _overlay: Overlay,
private _renderer: Renderer2,
      templateRef: TemplateRef<any>,
      viewContainerRef: ViewContainerRef,
private _scrollStrategy,
private _dir: Directionality) {
    this._templatePortal = new TemplatePortal(templateRef, viewContainerRef);
  }
/**
 * The associated overlay reference.
 * @return {?}
 */
get overlayRef(): OverlayRef {
    return this._overlayRef;
  }
/**
 * The element's layout direction.
 * @return {?}
 */
get dir(): Direction {
    return this._dir ? this._dir.value : 'ltr';
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._destroyOverlay();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (changes['open'] || changes['_deprecatedOpen']) {
      this.open ? this._attachOverlay() : this._detachOverlay();
    }
  }
/**
 * Creates an overlay
 * @return {?}
 */
private _createOverlay() {
    if (!this.positions || !this.positions.length) {
      this.positions = defaultPositionList;
    }

    this._overlayRef = this._overlay.create(this._buildConfig());
  }
/**
 * Builds the overlay config based on the directive's inputs
 * @return {?}
 */
private _buildConfig(): OverlayState {
    let /** @type {?} */ overlayConfig = new OverlayState();

    if (this.width || this.width === 0) {
      overlayConfig.width = this.width;
    }

    if (this.height || this.height === 0) {
      overlayConfig.height = this.height;
    }

    if (this.minWidth || this.minWidth === 0) {
      overlayConfig.minWidth = this.minWidth;
    }

    if (this.minHeight || this.minHeight === 0) {
      overlayConfig.minHeight = this.minHeight;
    }

    overlayConfig.hasBackdrop = this.hasBackdrop;

    if (this.backdropClass) {
      overlayConfig.backdropClass = this.backdropClass;
    }

    this._position = /** @type {?} */(( this._createPositionStrategy() as ConnectedPositionStrategy));
    overlayConfig.positionStrategy = this._position;
    overlayConfig.scrollStrategy = this.scrollStrategy;

    return overlayConfig;
  }
/**
 * Returns the position strategy of the overlay to be set on the overlay config
 * @return {?}
 */
private _createPositionStrategy(): ConnectedPositionStrategy {
    const /** @type {?} */ pos = this.positions[0];
    const /** @type {?} */ originPoint = {originX: pos.originX, originY: pos.originY};
    const /** @type {?} */ overlayPoint = {overlayX: pos.overlayX, overlayY: pos.overlayY};

    const /** @type {?} */ strategy = this._overlay.position()
      .connectedTo(this.origin.elementRef, originPoint, overlayPoint)
      .withOffsetX(this.offsetX)
      .withOffsetY(this.offsetY);

    this._handlePositionChanges(strategy);

    return strategy;
  }
/**
 * @param {?} strategy
 * @return {?}
 */
private _handlePositionChanges(strategy: ConnectedPositionStrategy): void {
    for (let /** @type {?} */ i = 1; i < this.positions.length; i++) {
      strategy.withFallbackPosition(
          {originX: this.positions[i].originX, originY: this.positions[i].originY},
          {overlayX: this.positions[i].overlayX, overlayY: this.positions[i].overlayY}
      );
    }

    this._positionSubscription =
        strategy.onPositionChange.subscribe(pos => this.positionChange.emit(pos));
  }
/**
 * Attaches the overlay and subscribes to backdrop clicks if backdrop exists
 * @return {?}
 */
private _attachOverlay() {
    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._position.withDirection(this.dir);
    this._overlayRef.getState().direction = this.dir;
    this._initEscapeListener();

    if (!this._overlayRef.hasAttached()) {
      this._overlayRef.attach(this._templatePortal);
      this.attach.emit();
    }

    if (this.hasBackdrop) {
      this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
        this.backdropClick.emit();
      });
    }
  }
/**
 * Detaches the overlay and unsubscribes to backdrop clicks if backdrop exists
 * @return {?}
 */
private _detachOverlay() {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this.detach.emit();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
      this._backdropSubscription = null;
    }

    if (this._escapeListener) {
      this._escapeListener();
    }
  }
/**
 * Destroys the overlay created by this directive.
 * @return {?}
 */
private _destroyOverlay() {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
    }

    if (this._positionSubscription) {
      this._positionSubscription.unsubscribe();
    }

    if (this._escapeListener) {
      this._escapeListener();
    }
  }
/**
 * Sets the event listener that closes the overlay when pressing Escape.
 * @return {?}
 */
private _initEscapeListener() {
    this._escapeListener = this._renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.keyCode === ESCAPE) {
        this._detachOverlay();
      }
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]',
  exportAs: 'cdkConnectedOverlay'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Overlay, },
{type: Renderer2, },
{type: TemplateRef, },
{type: ViewContainerRef, },
{type: undefined, decorators: [{ type: Inject, args: [MD_CONNECTED_OVERLAY_SCROLL_STRATEGY, ] }, ]},
{type: Directionality, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'origin': [{ type: Input, args: ['cdkConnectedOverlayOrigin', ] },],
'positions': [{ type: Input, args: ['cdkConnectedOverlayPositions', ] },],
'offsetX': [{ type: Input, args: ['cdkConnectedOverlayOffsetX', ] },],
'offsetY': [{ type: Input, args: ['cdkConnectedOverlayOffsetY', ] },],
'width': [{ type: Input, args: ['cdkConnectedOverlayWidth', ] },],
'height': [{ type: Input, args: ['cdkConnectedOverlayHeight', ] },],
'minWidth': [{ type: Input, args: ['cdkConnectedOverlayMinWidth', ] },],
'minHeight': [{ type: Input, args: ['cdkConnectedOverlayMinHeight', ] },],
'backdropClass': [{ type: Input, args: ['cdkConnectedOverlayBackdropClass', ] },],
'scrollStrategy': [{ type: Input, args: ['cdkConnectedOverlayScrollStrategy', ] },],
'open': [{ type: Input, args: ['cdkConnectedOverlayOpen', ] },],
'hasBackdrop': [{ type: Input, args: ['cdkConnectedOverlayHasBackdrop', ] },],
'_deprecatedOrigin': [{ type: Input, args: ['origin', ] },],
'_deprecatedPositions': [{ type: Input, args: ['positions', ] },],
'_deprecatedOffsetX': [{ type: Input, args: ['offsetX', ] },],
'_deprecatedOffsetY': [{ type: Input, args: ['offsetY', ] },],
'_deprecatedWidth': [{ type: Input, args: ['width', ] },],
'_deprecatedHeight': [{ type: Input, args: ['height', ] },],
'_deprecatedMinWidth': [{ type: Input, args: ['minWidth', ] },],
'_deprecatedMinHeight': [{ type: Input, args: ['minHeight', ] },],
'_deprecatedBackdropClass': [{ type: Input, args: ['backdropClass', ] },],
'_deprecatedScrollStrategy': [{ type: Input, args: ['scrollStrategy', ] },],
'_deprecatedOpen': [{ type: Input, args: ['open', ] },],
'_deprecatedHasBackdrop': [{ type: Input, args: ['hasBackdrop', ] },],
'backdropClick': [{ type: Output },],
'positionChange': [{ type: Output },],
'attach': [{ type: Output },],
'detach': [{ type: Output },],
};
}

function ConnectedOverlayDirective_tsickle_Closure_declarations() {
/** @type {?} */
ConnectedOverlayDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ConnectedOverlayDirective.ctorParameters;
/** @type {?} */
ConnectedOverlayDirective.propDecorators;
/** @type {?} */
ConnectedOverlayDirective.prototype._overlayRef;
/** @type {?} */
ConnectedOverlayDirective.prototype._templatePortal;
/** @type {?} */
ConnectedOverlayDirective.prototype._hasBackdrop;
/** @type {?} */
ConnectedOverlayDirective.prototype._backdropSubscription;
/** @type {?} */
ConnectedOverlayDirective.prototype._positionSubscription;
/** @type {?} */
ConnectedOverlayDirective.prototype._offsetX;
/** @type {?} */
ConnectedOverlayDirective.prototype._offsetY;
/** @type {?} */
ConnectedOverlayDirective.prototype._position;
/** @type {?} */
ConnectedOverlayDirective.prototype._escapeListener;
/**
 * Origin for the connected overlay.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.origin;
/**
 * Registered connected position pairs.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.positions;
/**
 * The width of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.width;
/**
 * The height of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.height;
/**
 * The min width of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.minWidth;
/**
 * The min height of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.minHeight;
/**
 * The custom class to be set on the backdrop element.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.backdropClass;
/**
 * Strategy to be used when handling scroll events while the overlay is open.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.scrollStrategy;
/**
 * Whether the overlay is open.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.open;
/**
 * Event emitted when the backdrop is clicked.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.backdropClick;
/**
 * Event emitted when the position has changed.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.positionChange;
/**
 * Event emitted when the overlay has been attached.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.attach;
/**
 * Event emitted when the overlay has been detached.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.detach;
/** @type {?} */
ConnectedOverlayDirective.prototype._overlay;
/** @type {?} */
ConnectedOverlayDirective.prototype._renderer;
/** @type {?} */
ConnectedOverlayDirective.prototype._scrollStrategy;
/** @type {?} */
ConnectedOverlayDirective.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../src/cdk/overlay/overlay-directives.ts